import { BRAND_CLASSES } from '@/constants/theme'
import { getLabelClasses, type LabelSize } from './formLabelUtils'

export interface RadioGroupOption {
    /** Value for the option */
    value: string | number
    /** Display label for the option */
    label: string
    /** Whether this option is disabled */
    disabled?: boolean
    /** Optional description shown below the label */
    description?: string
}

export interface RadioGroupProps {
    /** Field label */
    label?: string
    /** Whether field is required */
    required?: boolean
    /** Name attribute for the radio group */
    name: string
    /** Error message to display */
    error?: string
    /** Helper text to display below the group */
    helperText?: string
    /** Options to display as radio buttons */
    options: RadioGroupOption[]
    /** Currently selected value */
    value?: string | number | null
    /** Callback when selection changes */
    onChange?: (value: string | number | null) => void
    /** Custom class name for container */
    containerClassName?: string
    /** Allow clicking selected radio to deselect */
    allowDeselect?: boolean
    /**
     * Layout mode. `cards` draws each option as a bordered card with its description, in a fieldset whose
     * legend names the group.
     */
    layout?: 'vertical' | 'grid' | 'cards'
    /** Cards per row from the small breakpoint up, for the `cards` layout. Narrow panels use 1. */
    cardColumns?: 1 | 2
    /** Label scale, matching the other form fields beside the group. */
    labelSize?: LabelSize
}

/**
 * RadioGroup component for single-select radio button groups.
 * Works with react-hook-form via Controller.
 *
 * @example
 * // Standalone usage
 * const [role, setRole] = useState<string | null>(null)
 *
 * <RadioGroup
 *   label="Role"
 *   name="role"
 *   options={[
 *     { value: 'admin', label: 'Admin' },
 *     { value: 'editor', label: 'Editor' },
 *   ]}
 *   value={role}
 *   onChange={setRole}
 *   allowDeselect
 * />
 *
 * @example
 * // With react-hook-form Controller
 * <Controller
 *   name="role"
 *   control={control}
 *   rules={{ required: 'Role is required' }}
 *   render={({ field, fieldState }) => (
 *     <RadioGroup
 *       label="Role"
 *       name="role"
 *       options={roleOptions}
 *       value={field.value}
 *       onChange={field.onChange}
 *       error={fieldState.error?.message}
 *     />
 *   )}
 * />
 */
export function RadioGroup({
    label,
    required,
    name,
    error,
    helperText,
    options,
    value,
    onChange,
    containerClassName = '',
    allowDeselect = false,
    layout = 'vertical',
    cardColumns = 2,
    labelSize,
}: RadioGroupProps) {
    const labelClasses = labelSize ? getLabelClasses(labelSize) : `block text-sm font-medium mb-2 ${BRAND_CLASSES.INPUT.LABEL}`
    const requiredMark = required && <span className={`${BRAND_CLASSES.INPUT.REQUIRED} ml-1`}>*</span>
    const footer = (
        <>
            {error && (
                <p className={`mt-1 text-sm ${BRAND_CLASSES.INPUT.ERROR_TEXT}`}>{error}</p>
            )}
            {!error && helperText && (
                <p className={`mt-1 text-xs ${BRAND_CLASSES.INPUT.HELPER}`}>{helperText}</p>
            )}
        </>
    )

    if (layout === 'cards') {
        return (
            <fieldset className={containerClassName}>
                {label && <legend className={labelClasses}>{label}{requiredMark}</legend>}
                <div className={`grid grid-cols-1 gap-3 ${cardColumns === 2 ? 'sm:grid-cols-2' : ''}`}>
                    {options.map(option => {
                        const isSelected = value === option.value
                        return (
                            <label
                                key={option.value}
                                className={`flex items-start gap-3 px-4 py-3.5 rounded-lg border transition-colors focus-within:ring-2 focus-within:ring-primary-500 ${option.disabled
                                    ? 'cursor-not-allowed opacity-60 border-slate-200 dark:border-slate-700'
                                    : isSelected
                                        ? 'cursor-pointer border-primary-600 ring-1 ring-primary-600 bg-primary-50 dark:bg-primary-900/20'
                                        : 'cursor-pointer border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 hover:border-slate-400 dark:hover:border-slate-500'}`}
                            >
                                <input
                                    type="radio"
                                    name={name}
                                    checked={isSelected}
                                    disabled={option.disabled}
                                    onChange={() => {
                                        if (!option.disabled) onChange?.(option.value)
                                    }}
                                    className="mt-0.5 w-4 h-4 flex-none border-slate-300 dark:border-slate-600 text-primary-600 focus:ring-0 focus:ring-offset-0"
                                />
                                <span className="min-w-0">
                                    <span className="block text-sm font-semibold text-slate-900 dark:text-white">{option.label}</span>
                                    {option.description && (
                                        <span className={`block mt-0.5 text-[13px] leading-snug ${BRAND_CLASSES.INPUT.HELPER}`}>{option.description}</span>
                                    )}
                                </span>
                            </label>
                        )
                    })}
                </div>
                {footer}
            </fieldset>
        )
    }

    return (
        <div className={containerClassName}>
            {label && (
                <label className={labelClasses}>
                    {label}
                    {requiredMark}
                </label>
            )}

            <div className={layout === 'grid' ? 'grid grid-cols-2 md:grid-cols-3 gap-3' : 'space-y-2'}>
                {options.map(option => {
                    const isSelected = value === option.value
                    return (
                        <label
                            key={option.value}
                            className={`flex items-start space-x-2 ${option.disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                            onClick={() => {
                                // Handle deselection: clicking an already-selected radio deselects it
                                if (allowDeselect && isSelected && !option.disabled) {
                                    onChange?.(null)
                                }
                            }}
                        >
                            <input
                                type="radio"
                                name={name}
                                checked={isSelected}
                                disabled={option.disabled}
                                onChange={() => {
                                    if (!option.disabled) {
                                        onChange?.(option.value)
                                    }
                                }}
                                className="mt-0.5 border-slate-300 dark:border-slate-600 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
                            />
                            <div>
                                <span className={`text-sm ${option.disabled ? 'text-slate-400 dark:text-slate-500' : 'text-slate-700 dark:text-slate-300'}`}>
                                    {option.label}
                                </span>
                                {option.description && (
                                    <p className={`text-xs ${BRAND_CLASSES.INPUT.HELPER}`}>{option.description}</p>
                                )}
                            </div>
                        </label>
                    )
                })}
            </div>

            {footer}
        </div>
    )
}
